import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, EMPTY } from 'rxjs';
import { switchMap, catchError, filter, take, tap } from 'rxjs/operators';
import { Injectable, Inject } from '@angular/core';
import { MsalService, MsalBroadcastService, MSAL_INTERCEPTOR_CONFIG, MsalInterceptorConfiguration } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, InteractionType, BrowserAuthError } from '@azure/msal-browser';
import { determineMSALScope } from '../common/scope-utility';
import { ProfileService } from '../services/profile.service';
import { LoggerService } from '../services/logger.service';

/**
 * Custom MSAL Interceptor that follows the crew-jumpseat-ui pattern.
 * Automatically adds Bearer tokens to outgoing HTTP requests.
 */
@Injectable()
export class CvvMsalInterceptor implements HttpInterceptor {
  private requestCounter = 0;

  constructor(
    @Inject(MSAL_INTERCEPTOR_CONFIG) private msalInterceptorConfig: MsalInterceptorConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private logger: LoggerService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const requestId = ++this.requestCounter;
    this.logger.debug(`[AuthFlow][Interceptor][${requestId}] incoming request: ${req.method} ${req.url}`, 'auth');

    // Skip authentication for static assets and config files
    if (this.shouldSkipAuthentication(req.url)) {
      this.logger.debug(`[AuthFlow][Interceptor][${requestId}] skipping auth for request: ${req.url}`, 'auth');
      return next.handle(req);
    }

    const serviceToken = ProfileService.servicePrincipalToken;
    if (serviceToken) {
      this.logger.debug(`[AuthFlow][Interceptor][${requestId}] using service principal token for: ${req.url}`, 'auth');
      const headers = req.headers.set('Authorization', `Bearer ${serviceToken}`);
      return next.handle(req.clone({ headers }));
    }

    const scopes = this.getScopesForEndpoint(req.url);

    if (!scopes || scopes.length === 0) {
      this.logger.warn(`[AuthFlow][Interceptor][${requestId}] no scopes found, passing through: ${req.url}`);
      return next.handle(req);
    }

    // Wait for any in-progress interaction to complete before checking account
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      switchMap(() => {
        // Check account AFTER interaction completes so redirect callback has time to populate accounts
        const account = this.getOrSetAccount(requestId);

        this.logger.debug(
          `[AuthFlow][Interceptor][${requestId}] scopes=${JSON.stringify(scopes)} activeAccount=${account?.username || 'none'} allAccounts=${this.authService.instance.getAllAccounts().length}`,
          'auth'
        );

        if (!account) {
          this.logger.warn(`[AuthFlow][Interceptor][${requestId}] no account available yet for ${req.url}; dropping request to avoid redirect loop`);
          return EMPTY;
        }

        this.logger.debug(`[AuthFlow][Interceptor][${requestId}] interaction status is NONE, acquiring token silently`, 'auth');
        // Acquire token silently and attach to request
        return this.authService.acquireTokenSilent({ scopes, account }).pipe(
          tap((result: AuthenticationResult) => {
            this.logger.debug(
              `[AuthFlow][Interceptor][${requestId}] acquireTokenSilent success for ${req.url}; expiresOn=${result?.expiresOn}`,
              'auth'
            );
          }),
          catchError((error) => {
            this.logger.error(`[AuthFlow][Interceptor][${requestId}] acquireTokenSilent failed for ${req.url}`, error);
            // Check if interaction is already in progress
            if (error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress') {
              this.logger.warn(`[AuthFlow][Interceptor][${requestId}] interaction_in_progress for ${req.url}; dropping request`);
              return EMPTY;
            }

            if (error instanceof BrowserAuthError && error.errorCode === 'no_account_error') {
              this.logger.warn(
                `[AuthFlow][Interceptor][${requestId}] no_account_error for ${req.url}; currentUrl=${window.location.href}`
              );

              if (this.hasAuthCodeInUrl()) {
                this.logger.warn(
                  `[AuthFlow][Interceptor][${requestId}] auth code/hash detected in URL; skipping redirect fallback to prevent loop`
                );
                return EMPTY;
              }
            }
            
            // If silent acquisition fails and no interaction in progress, try redirect
            if (this.msalInterceptorConfig.interactionType === InteractionType.Popup) {
              this.logger.warn(`[AuthFlow][Interceptor][${requestId}] falling back to popup for ${req.url}`);
              return this.authService.acquireTokenPopup({ 
                ...this.msalInterceptorConfig.authRequest, 
                scopes 
              });
            }
            
            const redirectStartPage = this.getSafeRedirectStartPage();
            this.logger.warn(
              `[AuthFlow][Interceptor][${requestId}] falling back to redirect for ${req.url}; redirectStartPage=${redirectStartPage}`
            );
            this.authService.acquireTokenRedirect({ 
              ...this.msalInterceptorConfig.authRequest, 
              scopes, 
              redirectStartPage 
            });
            return EMPTY;
          }),
          switchMap((result: AuthenticationResult) => {
            const headers = req.headers.set('Authorization', `Bearer ${result.accessToken}`);
            const requestClone = req.clone({ headers });
            this.logger.debug(`[AuthFlow][Interceptor][${requestId}] forwarding authorized request: ${req.url}`, 'auth');
            return next.handle(requestClone);
          })
        );
      })
    );
  }

  /**
   * Determines if a request should skip authentication.
   */
  private shouldSkipAuthentication(url: string): boolean {
    const skipPatterns = [
      '/assets/',
      '.json',
      '.svg',
      '.png',
      '.jpg',
      '.ico'
    ];
    
    return skipPatterns.some(pattern => url.includes(pattern));
  }

  /**
   * Gets the appropriate scopes for the endpoint.
   * Uses dynamic scope determination based on environment.
   */
  private getScopesForEndpoint(endpoint: string): Array<string> | null {
    // Use dynamic scope based on current environment
    return [determineMSALScope()];
  }

  private getOrSetAccount(requestId: number) {
    const activeAccount = this.authService.instance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    const allAccounts = this.authService.instance.getAllAccounts();
    if (allAccounts.length > 0) {
      this.authService.instance.setActiveAccount(allAccounts[0]);
      this.logger.debug(
        `[AuthFlow][Interceptor][${requestId}] active account was empty; set to ${allAccounts[0]?.username || 'unknown'}`,
        'auth'
      );
      return allAccounts[0];
    }

    return null;
  }

  private getSafeRedirectStartPage(): string {
    return `${window.location.origin}${window.location.pathname}${window.location.search}`;
  }

  private hasAuthCodeInUrl(): boolean {
    const hash = window.location.hash || '';
    return hash.includes('code=') || hash.includes('id_token=') || hash.includes('access_token=');
  }
}
